import { useState } from "react";
import { usePage, Link } from "@inertiajs/react";
import { Bar, BarChart, Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon } from "@heroicons/react/16/solid";
import type { InertiaProps } from "@shared";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@components/ui/chart";

// Use generated types from RSwag
export type ProjectPerformanceProps =
  InertiaProps.paths["/projects/{uuid}/performance"]["get"]["responses"]["200"]["content"]["application/json"];

// Derive nested types from generated type
type Metrics = ProjectPerformanceProps["metrics"][string];
type Summary = Metrics["summary"];
type Trend = NonNullable<Summary["ad_spend_trend"]>;
type TimeSeries = Metrics["impressions"];
type LeadMagnetMetric = Metrics["lead_magnets"]["impressions"];
type LeadMagnetSeries = LeadMagnetMetric["series"][number];

// One color per magnet, shared across all three lead-magnet charts so a magnet
// reads as the same color everywhere. "Other" (the capped remainder) is always
// gray so it never competes with a named magnet.
const MAGNET_PALETTE = ["#0D9488", "#6366F1", "#F59E0B", "#EC4899", "#0EA5E9"];
const OTHER_COLOR = "#94A3B8";

function colorForSeries(series: LeadMagnetSeries, index: number): string {
  return series.slug === "other" ? OTHER_COLOR : MAGNET_PALETTE[index % MAGNET_PALETTE.length];
}

function magnetChartConfig(series: LeadMagnetSeries[]): ChartConfig {
  const config: ChartConfig = {};
  series.forEach((s, i) => {
    config[s.slug] = { label: s.title, color: colorForSeries(s, i) };
  });
  return config;
}

function formatCount(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(Math.round(value));
}

// Recharts wants one row per date with a key per series; the backend gives us
// one series per magnet with a value per date, so transpose.
function toMagnetRows(metric: LeadMagnetMetric): Array<Record<string, string | number>> {
  return metric.dates.map((date, i) => {
    const row: Record<string, string | number> = { displayDate: formatDate(date) };
    metric.series.forEach((s) => {
      row[s.slug] = s.data[i] ?? 0;
    });
    return row;
  });
}

type DaysKey = "7" | "30" | "90" | "0";
const DEFAULT_DAYS: DaysKey = "30";

// Colors matching Launch10 design system
const PRIMARY_COLOR = "#0D9488"; // teal

// Calculate a nice Y-axis max with headroom (roughly 1.25x data max, rounded to nice intervals)
function getNiceYAxisMax(dataMax: number): number {
  if (dataMax === 0) return 100;
  const target = dataMax * 1.25;
  const magnitude = Math.pow(10, Math.floor(Math.log10(target)));
  const normalized = target / magnitude;
  // Round up to nearest 1, 2, 2.5, 5, or 10
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 2.5) nice = 2.5;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

export default function ProjectPerformance() {
  const {
    project,
    metrics: allMetrics,
    date_range_options,
  } = usePage<ProjectPerformanceProps>().props;

  const [selectedDays, setSelectedDays] = useState<DaysKey>(DEFAULT_DAYS);

  // Get metrics for selected date range
  const metrics = allMetrics[selectedDays];
  const hasData = metrics.has_data;
  // Lead magnets carry their own data flag — this project's site can have
  // magnet activity even when it has no ad data yet.
  const leadMagnetsHaveData = metrics.lead_magnets.has_data;

  const handleDateRangeChange = (days: number) => {
    setSelectedDays(String(days) as DaysKey);
  };

  // Find the selected date range option for display
  const selectedOption = date_range_options.find((opt) => String(opt.days) === selectedDays);
  const dateRangeLabel = selectedOption?.label || "Last 30 days";

  return (
    <main className="min-h-screen bg-[#FAFAF9]">
      <div className="pl-4 pr-[3%] py-6 lg:pl-12 lg:pr-[5%] lg:py-10">
        {/* Header */}
        <div className="mb-8">
          <Link
            href="/projects"
            className="text-xs text-base-400 hover:text-base-600 inline-flex items-center gap-1 mb-2"
            data-testid="back-link"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="opacity-60">
              <path
                d="M10 12L6 8L10 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Projects
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-['IBM_Plex_Serif'] text-[28px] font-semibold text-[#2E3238]">
                Performance
              </h1>
              <p className="text-base-400 text-sm mt-1">{project.name}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-base-500 whitespace-nowrap">Filter by:</span>
              <select
                value={selectedDays}
                onChange={(e) => handleDateRangeChange(Number(e.target.value))}
                className="text-sm border border-neutral-300 rounded-md px-3 py-1.5 bg-white min-w-[145px]"
                data-testid="date-range-filter"
              >
                {date_range_options.map((option) => (
                  <option key={option.days} value={option.days}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Info Banner - shown when no data */}
        {!hasData && <NoDataBanner />}

        {/* Summary Cards */}
        <section className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              title="Ad Spend"
              description="Total cost for date range"
              value={hasData ? `$${Number(metrics.summary.ad_spend ?? 0).toFixed(2)}` : null}
              trend={hasData ? metrics.summary.ad_spend_trend : undefined}
              invertTrend={true}
              highlighted={true}
              testId="ad-spend-card"
            />
            <SummaryCard
              title="Leads"
              description="Form submissions not attributed to ads"
              value={hasData ? String(metrics.summary.leads ?? 0) : null}
              trend={hasData ? metrics.summary.leads_trend : undefined}
              link={
                hasData
                  ? {
                      href: `/projects/${project.uuid}/leads`,
                      label: "View Leads",
                    }
                  : undefined
              }
              testId="leads-card"
            />
            <SummaryCard
              title="Avg Cost per Lead"
              description="Spend required to generate a lead"
              value={
                hasData && metrics.summary.cpl != null
                  ? `$${Number(metrics.summary.cpl).toFixed(2)}`
                  : null
              }
              trend={hasData ? metrics.summary.cpl_trend : undefined}
              invertTrend={true}
              testId="cpl-card"
            />
            <SummaryCard
              title="Return on Ad Spend"
              description="Revenue earned from ad spend"
              value={
                hasData && metrics.summary.roas != null
                  ? `${Number(metrics.summary.roas).toFixed(2)}x`
                  : null
              }
              trend={hasData ? metrics.summary.roas_trend : undefined}
              testId="roas-card"
            />
          </div>
        </section>

        {/* Charts */}
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <BarChartCard
              title="Impressions"
              data={metrics.impressions}
              dateRange={dateRangeLabel}
              hasData={hasData}
              testId="impressions-chart"
            />
            <BarChartCard
              title="Ad Clicks"
              data={metrics.clicks}
              dateRange={dateRangeLabel}
              hasData={hasData}
              testId="clicks-chart"
            />
            <LineChartCard
              title="Click-Through Rate"
              data={metrics.ctr}
              dateRange={dateRangeLabel}
              valueFormatter={(v) => `${(v * 100).toFixed(1)}%`}
              hasData={hasData}
              testId="ctr-chart"
            />
          </div>
        </section>

        {/* Lead Magnets — workspace-wide, independent of ad data */}
        <section className="mt-8">
          <div className="mb-4">
            <h2 className="font-['IBM_Plex_Serif'] text-[20px] font-semibold text-[#2E3238]">
              Lead Magnets
            </h2>
            <p className="text-base-400 text-sm mt-1">
              Email-gated downloads on this project's site
            </p>
          </div>
          {leadMagnetsHaveData && metrics.lead_magnets.impressions.series.length > 0 && (
            <LeadMagnetLegend series={metrics.lead_magnets.impressions.series} />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MagnetMultiLineCard
              title="Magnet Impressions"
              metric={metrics.lead_magnets.impressions}
              dateRange={dateRangeLabel}
              valueFormatter={formatCount}
              hasData={leadMagnetsHaveData}
              testId="lead-magnet-impressions-chart"
            />
            <MagnetMultiLineCard
              title="Downloads"
              metric={metrics.lead_magnets.downloads}
              dateRange={dateRangeLabel}
              valueFormatter={formatCount}
              hasData={leadMagnetsHaveData}
              testId="lead-magnet-downloads-chart"
            />
            <MagnetMultiLineCard
              title="Conversion Rate"
              metric={metrics.lead_magnets.conversion_rate}
              dateRange={dateRangeLabel}
              valueFormatter={(v) => `${(v * 100).toFixed(1)}%`}
              hasData={leadMagnetsHaveData}
              testId="lead-magnet-conversion-chart"
            />
          </div>

          {/* Exact per-magnet numbers — the chart legend's companion */}
          {metrics.lead_magnets.magnets.length > 0 && (
            <LeadMagnetBreakdown magnets={metrics.lead_magnets.magnets} />
          )}
        </section>
      </div>
    </main>
  );
}

function LeadMagnetLegend({ series }: { series: LeadMagnetSeries[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3" data-testid="lead-magnet-legend">
      {series.map((s, i) => (
        <span key={s.slug} className="flex items-center gap-1.5 text-xs text-[#2E3238]">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
            style={{ backgroundColor: colorForSeries(s, i) }}
          />
          {s.title}
        </span>
      ))}
    </div>
  );
}

function MagnetChartCardShell({
  title,
  metric,
  dateRange,
  hasData,
  testId,
  children,
}: {
  title: string;
  metric: LeadMagnetMetric;
  dateRange: string;
  hasData: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  const startDate = metric.dates[0];
  const endDate = metric.dates[metric.dates.length - 1];
  const dateSubtitle =
    startDate && endDate
      ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
      : dateRange;

  const trendPercent = metric.totals.trend_percent;
  const trendDirection = metric.totals.trend_direction;

  return (
    <div
      className="rounded-2xl border border-neutral-300 bg-white p-6 min-h-[359px] flex flex-col"
      data-testid={testId}
    >
      <div className="mb-6">
        <h3 className="text-sm font-medium text-[#0F1113]">{title}</h3>
        <p className="text-xs text-[#96989B]">{dateSubtitle}</p>
        {metric.data_delay && <DataDelayIndicator type={metric.data_delay} />}
      </div>

      {hasData ? (
        <div className="flex-1 flex flex-col">
          {children}
          {trendDirection !== "flat" && (
            <p className="text-xs mt-auto pt-2 flex items-center gap-1 text-[#96989B]">
              Trending {trendDirection} by {trendPercent.toFixed(1)}% this week
              <span aria-hidden="true">{trendDirection === "up" ? "↗" : "↘"}</span>
            </p>
          )}
        </div>
      ) : (
        <EmptyChartState />
      )}
    </div>
  );
}

function MagnetMultiLineCard({
  title,
  metric,
  dateRange,
  valueFormatter,
  hasData,
  testId,
}: {
  title: string;
  metric: LeadMagnetMetric;
  dateRange: string;
  valueFormatter: (value: number) => string;
  hasData: boolean;
  testId?: string;
}) {
  const config = magnetChartConfig(metric.series);
  const rows = toMagnetRows(metric);
  const seriesMax = Math.max(0, ...metric.series.flatMap((s) => s.data));
  const yAxisMax = getNiceYAxisMax(seriesMax);

  return (
    <MagnetChartCardShell
      title={title}
      metric={metric}
      dateRange={dateRange}
      hasData={hasData}
      testId={testId}
    >
      <ChartContainer config={config} className="flex-1 w-full min-h-0">
        <LineChart data={rows} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
          <XAxis
            dataKey="displayDate"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 9, fill: "#9CA3AF" }}
            tickMargin={8}
            minTickGap={30}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 9, fill: "#9CA3AF" }}
            tickMargin={4}
            domain={[0, yAxisMax]}
            tickFormatter={(value) => valueFormatter(value)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => label}
                formatter={(value, name, item) => (
                  <>
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: (item as { color?: string })?.color }}
                    />
                    <span className="flex flex-1 items-center justify-between gap-3 leading-none">
                      <span className="text-neutral-500">{String(name)}</span>
                      <span className="font-mono font-medium tabular-nums text-[#2E3238]">
                        {valueFormatter(value as number)}
                      </span>
                    </span>
                  </>
                )}
              />
            }
          />
          {metric.series.map((s, i) => (
            <Line
              key={s.slug}
              type="monotone"
              dataKey={s.slug}
              name={s.title}
              stroke={colorForSeries(s, i)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </MagnetChartCardShell>
  );
}

function LeadMagnetBreakdown({ magnets }: { magnets: Metrics["lead_magnets"]["magnets"] }) {
  return (
    <div
      className="mt-4 rounded-2xl border border-neutral-300 bg-white overflow-hidden"
      data-testid="lead-magnet-breakdown"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-xs text-[#96989B]">
            <th className="text-left font-medium px-6 py-3">Lead Magnet</th>
            <th className="text-right font-medium px-6 py-3">Impressions</th>
            <th className="text-right font-medium px-6 py-3">Downloads</th>
            <th className="text-right font-medium px-6 py-3">Conversion</th>
          </tr>
        </thead>
        <tbody>
          {magnets.map((magnet) => (
            <tr
              key={magnet.id}
              className="border-b border-neutral-100 last:border-0"
              data-testid={`lead-magnet-row-${magnet.slug}`}
            >
              <td className="px-6 py-3">
                <span className="font-medium text-[#2E3238]">{magnet.title}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wide text-[#96989B]">
                  {magnet.kind}
                </span>
              </td>
              <td className="px-6 py-3 text-right tabular-nums text-[#2E3238]">
                {magnet.impressions.toLocaleString()}
              </td>
              <td className="px-6 py-3 text-right tabular-nums text-[#2E3238]">
                {magnet.downloads.toLocaleString()}
              </td>
              <td className="px-6 py-3 text-right tabular-nums text-[#2E3238]">
                {magnet.conversion_rate == null
                  ? "—"
                  : `${(magnet.conversion_rate * 100).toFixed(1)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NoDataBanner() {
  return (
    <div
      className="mb-8 rounded-lg border border-neutral-300 bg-[#FAFAF9] p-4 flex items-start gap-3"
      data-testid="no-data-banner"
    >
      <div className="flex-shrink-0 mt-0.5">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-[#2E3238]"
        >
          <path
            d="M8 14.667A6.667 6.667 0 1 0 8 1.333a6.667 6.667 0 0 0 0 13.334ZM8 5.333V8M8 10.667h.007"
            stroke="currentColor"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-base text-[#2E3238] leading-5">Not enough data yet</h3>
        <p className="text-sm text-[#2E3238] leading-[18px] mt-1">
          Check back in 24–48 hours after your campaign starts running. Google Ads typically needs a
          day to report initial metrics.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  description,
  value,
  link,
  trend,
  invertTrend = false,
  highlighted = false,
  testId,
}: {
  title: string;
  description: string;
  value: string | null;
  link?: { href: string; label: string };
  trend?: Trend;
  invertTrend?: boolean;
  highlighted?: boolean;
  testId?: string;
}) {
  const isEmpty = value === null;

  // Determine if the trend is "good" based on direction and whether it's inverted
  // invertTrend=true means down is good (e.g., Ad Spend, CPL)
  const isGoodTrend =
    trend?.direction === "flat"
      ? null
      : invertTrend
        ? trend?.direction === "down"
        : trend?.direction === "up";

  // Determine background color based on trend
  const getTrendBgColor = () => {
    if (trend?.direction === "flat") return "bg-neutral-100";
    return isGoodTrend ? "bg-success-100" : "bg-secondary-100";
  };

  const borderClass = highlighted ? "border-primary-500" : "border-neutral-300";

  return (
    <div className={`rounded-2xl border ${borderClass} bg-white p-4 relative flex flex-col`} data-testid={testId}>
      {/* Trend icon in top right */}
      {trend && !isEmpty && (
        <div className="absolute top-4 right-4">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${getTrendBgColor()}`}
          >
            <TrendIcon direction={trend.direction} isGood={isGoodTrend ?? false} />
          </div>
        </div>
      )}

      <div className="pr-10">
        <h3 className="font-sans text-sm font-medium text-[#2E3238]">{title}</h3>
        <p className="text-xs text-[#96989B]">{description}</p>
      </div>

      {isEmpty ? (
        <div className="flex items-center gap-2 mt-auto pt-4">
          <div className="w-[33px] h-[33px] rounded-full bg-neutral-100 flex items-center justify-center">
            <ChartBarIcon size={13} />
          </div>
          <span className="text-xs text-[#96989B]">No data available yet</span>
        </div>
      ) : (
        <div className="mt-auto pt-4 flex items-end justify-between">
          <p className="text-lg font-semibold text-[#2E3238]" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
          {link && (
            <Link
              href={link.href}
              className="text-xs font-medium text-base-600 hover:text-base-600 inline-flex items-center gap-1"
              data-testid="view-leads-link"
            >
              {link.label}
              <span aria-hidden="true">&rarr;</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function TrendIcon({ direction, isGood }: { direction: "up" | "down" | "flat"; isGood: boolean }) {
  const colorClass =
    direction === "flat" ? "text-neutral-500" : isGood ? "text-success-700" : "text-secondary-700";

  if (direction === "up") {
    return <ArrowTrendingUpIcon className={`w-4 h-4 ${colorClass}`} />;
  }

  if (direction === "down") {
    return <ArrowTrendingDownIcon className={`w-4 h-4 ${colorClass}`} />;
  }

  // Flat trend
  return <MinusIcon className={`w-4 h-4 ${colorClass}`} />;
}

function BarChartCard({
  title,
  data,
  dateRange,
  hasData,
  testId,
}: {
  title: string;
  data: TimeSeries;
  dateRange: string;
  hasData: boolean;
  testId?: string;
}) {
  const chartData = data.dates.map((date, index) => ({
    date,
    displayDate: formatDate(date),
    value: data.data[index] || 0,
  }));

  const dataMax = Math.max(...chartData.map((d) => d.value));
  const yAxisMax = getNiceYAxisMax(dataMax);

  const chartConfig: ChartConfig = {
    value: {
      label: title,
      color: PRIMARY_COLOR,
    },
  };

  // Calculate date range for subtitle
  const startDate = data.dates[0];
  const endDate = data.dates[data.dates.length - 1];
  const dateSubtitle =
    startDate && endDate
      ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
      : dateRange;

  const trendPercent = data.totals.trend_percent;
  const trendDirection = data.totals.trend_direction;

  return (
    <div className="rounded-2xl border border-neutral-300 bg-white p-6 min-h-[359px] flex flex-col" data-testid={testId}>
      <div className="mb-6">
        <h3 className="text-sm font-medium text-[#0F1113]">{title}</h3>
        <p className="text-xs text-[#96989B]">{dateSubtitle}</p>
        {data.data_delay && <DataDelayIndicator type={data.data_delay} />}
      </div>

      {hasData ? (
        <div className="flex-1 flex flex-col">
          {chartData.length > 0 && (
            <div className="flex-1 flex flex-col">
              <ChartContainer config={chartConfig} className="flex-1 w-full min-h-0">
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
                  <XAxis
                    dataKey="displayDate"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                    tickMargin={8}
                    minTickGap={30}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                    tickMargin={4}
                    domain={[0, yAxisMax]}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                    }
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent labelFormatter={(label) => label} />}
                  />
                  <Bar dataKey="value" fill={PRIMARY_COLOR} radius={[2, 2, 0, 0]} maxBarSize={20} />
                </BarChart>
              </ChartContainer>

              {trendDirection !== "flat" && (
                <p className="text-xs mt-auto pt-2 flex items-center gap-1 text-[#96989B]">
                  Trending {trendDirection} by {trendPercent.toFixed(1)}% this week
                  <span aria-hidden="true">{trendDirection === "up" ? "↗" : "↘"}</span>
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <EmptyChartState />
      )}
    </div>
  );
}

function LineChartCard({
  title,
  data,
  dateRange,
  valueFormatter,
  hasData,
  testId,
}: {
  title: string;
  data: TimeSeries;
  dateRange: string;
  valueFormatter: (value: number) => string;
  hasData: boolean;
  testId?: string;
}) {
  const chartData = data.dates.map((date, index) => ({
    date,
    displayDate: formatDate(date),
    value: data.data[index] || 0,
  }));

  const dataMax = Math.max(...chartData.map((d) => d.value));
  const yAxisMax = getNiceYAxisMax(dataMax);

  const chartConfig: ChartConfig = {
    value: {
      label: title,
      color: PRIMARY_COLOR,
    },
  };

  // Calculate date range for subtitle
  const startDate = data.dates[0];
  const endDate = data.dates[data.dates.length - 1];
  const dateSubtitle =
    startDate && endDate
      ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
      : dateRange;

  const trendPercent = data.totals.trend_percent;
  const trendDirection = data.totals.trend_direction;

  return (
    <div className="rounded-2xl border border-neutral-300 bg-white p-6 min-h-[359px] flex flex-col" data-testid={testId}>
      <div className="mb-6">
        <h3 className="text-sm font-medium text-[#0F1113]">{title}</h3>
        <p className="text-xs text-[#96989B]">{dateSubtitle}</p>
        {data.data_delay && <DataDelayIndicator type={data.data_delay} />}
      </div>

      {hasData ? (
        <div className="flex-1 flex flex-col">
          {chartData.length > 0 && (
            <div className="flex-1 flex flex-col">
              <ChartContainer config={chartConfig} className="flex-1 w-full min-h-0">
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
                  <XAxis
                    dataKey="displayDate"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                    tickMargin={8}
                    minTickGap={30}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                    tickMargin={4}
                    domain={[0, yAxisMax]}
                    tickFormatter={(value) => valueFormatter(value)}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(label) => label}
                        formatter={(value) => [valueFormatter(value as number), title]}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={PRIMARY_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ChartContainer>

              {trendDirection !== "flat" && (
                <p className="text-xs mt-auto pt-2 flex items-center gap-1 text-[#96989B]">
                  Trending {trendDirection} by {trendPercent.toFixed(1)}% this week
                  <span aria-hidden="true">{trendDirection === "up" ? "↗" : "↘"}</span>
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <EmptyChartState />
      )}
    </div>
  );
}

function EmptyChartState() {
  return (
    <div className="h-[200px] flex flex-col items-center justify-center gap-3">
      <div className="w-[60px] h-[60px] rounded-full bg-neutral-100 flex items-center justify-center">
        <ChartBarIcon size={24} />
      </div>
      <span className="text-xs text-[#96989B]">No data available yet</span>
    </div>
  );
}

function DataDelayIndicator({ type }: { type: string }) {
  const delayText = type === "ads" ? "Data may be 2-4 hours delayed" : "Data may be 15 minutes delayed";

  return (
    <p className="text-[10px] text-base-200 mt-1">{delayText}</p>
  );
}

function ChartBarIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-neutral-400"
    >
      <rect x="2" y="11" width="4" height="7" rx="1" fill="currentColor" />
      <rect x="8" y="7" width="4" height="11" rx="1" fill="currentColor" />
      <rect x="14" y="3" width="4" height="15" rx="1" fill="currentColor" />
    </svg>
  );
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateShort(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
